import { useCallback, useState, useEffect } from 'react';
import { z } from 'zod';

import CompletionsProgressions from '../components/side-panel/CompletionsProgressions';
import PastDue from '../components/side-panel/PastDue';
import Revisions from '../components/side-panel/Revisions';
import EnrolmentHealth from '../components/side-panel/EnrolmentHealth';
import DueNextMonth from '../components/side-panel/DueNextMonth';
import SemesterProgress from '../components/side-panel/SemesterProgress';
import Engagement from '../components/side-panel/Engagement';
import TwoWeekEngagement from '../components/side-panel/TwoWeekEngagement';
import WorkPlacements from '../components/side-panel/WorkPlacements';
import type { DashboardContextType } from './useDashboardContext';

const SIDE_PANEL_CONTENT_MAP = {
  CompletionsProgressions,
  PastDue,
  Revisions,
  EnrolmentHealth,
  DueNextMonth,
  SemesterProgress,
  Engagement,
  TwoWeekEngagement,
  WorkPlacements,
} as const satisfies Record<string, React.ComponentType>;

type PanelKey = keyof typeof SIDE_PANEL_CONTENT_MAP;
const panelKeys = Object.keys(SIDE_PANEL_CONTENT_MAP) as [PanelKey, ...PanelKey[]];

const storedPanelStateSchema = z.object({
  key: z.enum(panelKeys),
  props: z.record(z.string(), z.unknown()).default({}),
});

type PanelState = {
  component: React.ComponentType;
  props: Record<string, unknown>;
} | null;

const LOCAL_STORAGE_KEY = 'dashboard_side_panel_state';

function useSidePanel(): DashboardContextType {
  const [panelState, setPanelState] = useState<PanelState | null>(null);

  useEffect(() => {
    const storedPanelState = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedPanelState !== null) {
      const parsed = storedPanelStateSchema.safeParse(JSON.parse(storedPanelState));
      if (parsed.success) {
        setPanelState({
          component: SIDE_PANEL_CONTENT_MAP[parsed.data.key],
          props: parsed.data.props,
        });
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  }, []);

  const openPanel = useCallback<DashboardContextType['openPanel']>((key, props = {}) => {
    setPanelState({ component: SIDE_PANEL_CONTENT_MAP[key], props });
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ key, props }));
  }, []);

  const closePanel = useCallback(() => {
    setPanelState(null);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  }, []);

  return {
    PanelContentComponent: panelState?.component ?? null,
    panelProps: panelState?.props ?? null,
    openPanel,
    closePanel,
  };
}

export default useSidePanel;
